"use client";

import * as React from "react";
import { CheckIcon, ClipboardIcon, SuccessCheckIcon } from "../icons";
import type { GasEstimate, WithdrawPosition } from "../../types";
import {
  formatGasLimit,
  formatGweiFromWei,
  formatNativeFeeFromWei,
  formatPortfolioTokenAmount,
  formatWalletAddress,
  rewardCardFitLevel,
  shortenTransactionHash,
} from "../../utils";

export type WithdrawDialogStatus =
  | "confirm"
  | "submitting"
  | "success"
  | "error";

export type WithdrawGasEstimateStatus =
  | "idle"
  | "loading"
  | "ready"
  | "error"
  | "unavailable";

/** 다이얼로그 토큰 리스트 항목 = 출금 가능 포지션 + 표시용 아이콘. */
export interface WithdrawDialogPosition extends WithdrawPosition {
  icon?: string;
}

interface WithdrawDialogProps {
  open: boolean;
  status: WithdrawDialogStatus;
  /** 출금 가능 포지션 목록. cross-points My-Point 모달의 토큰 리스트에 해당. */
  positions: WithdrawDialogPosition[];
  /** 열릴 때 선택해 둘 풀 주소. 없으면 첫 포지션. */
  initialPoolAddress?: string;
  walletAddress: string;
  txHash?: `0x${string}`;
  error?: string;
  copied?: boolean;
  /**
   * 선택 포지션·입력 금액(raw wei) 기준 가스 추정. 미주입이면
   * "Wallet estimate"로 표기하고 지갑 추정에 위임한다.
   */
  estimateWithdraw?: (
    position: WithdrawDialogPosition,
    amountRaw: bigint,
  ) => Promise<GasEstimate | null>;
  onClose: () => void;
  onConfirm: (
    position: WithdrawDialogPosition,
    amountRaw: bigint,
    estimate: GasEstimate | null,
  ) => void;
  onCopyHash: () => void;
  /** withdraw 퍼널 스텝 리포터 (`useWithdrawFlow().trackFunnelOnce`). */
  trackFunnel?: (step: string) => void;
  /** 가스비 표시용 네이티브 토큰 심볼 (info API 기준, 예: tONE/ONE). */
  nativeSymbol?: string;
}

const GAS_ESTIMATE_DEBOUNCE_MS = 400;

function formatFeeValue(
  estimate: GasEstimate | null,
  status: WithdrawGasEstimateStatus,
  nativeSymbol?: string,
) {
  if (status === "loading") return "Estimating...";
  if (!estimate || status === "error" || status === "unavailable") {
    return "Wallet estimate";
  }

  const feePerGas = estimate.maxFeePerGas ?? estimate.gasPrice;
  if (!feePerGas || feePerGas <= 0n || estimate.gasLimit <= 0n) {
    return "Wallet estimate";
  }
  return `${formatNativeFeeFromWei(estimate.gasLimit * feePerGas)} ${nativeSymbol ?? ""}`.trim();
}

function formatGasMetric(
  value: string,
  unit: string,
  status: WithdrawGasEstimateStatus,
) {
  if (status === "loading") return "Estimating...";
  if (status === "error" || status === "unavailable") return "-";
  return `${value} ${unit}`.trim();
}

function TokenBadge({
  position,
  size,
}: {
  position: WithdrawDialogPosition;
  size?: number;
}) {
  if (position.icon) {
    return <img src={position.icon} alt="" width={size} height={size} />;
  }
  return <span>{position.tokenSymbol.slice(0, 1)}</span>;
}

/**
 * cross-points My-Point의 withdraw 모달을 포트폴리오 안으로 옮긴 다이얼로그.
 * 토큰 리스트에서 예치 토큰을 고르면 **예치 전량**을 출금 대상으로 고정하고,
 * 확인 문구 → 가스 추정 → 지갑 확인 → Tx Hash 표기까지 처리한다 (부분 출금
 * 없음). 상태 머신(status)은 호출 섹션이 소유하고, 이 컴포넌트는 토큰
 * 선택과 가스 추정 요청만 로컬로 관리한다.
 */
export function WithdrawDialog({
  open,
  status,
  positions,
  initialPoolAddress,
  walletAddress,
  txHash,
  error,
  copied = false,
  estimateWithdraw,
  onClose,
  onConfirm,
  onCopyHash,
  trackFunnel,
  nativeSymbol,
}: WithdrawDialogProps) {
  const [gasEstimate, setGasEstimate] = React.useState<GasEstimate | null>(
    null,
  );
  const [gasStatus, setGasStatus] =
    React.useState<WithdrawGasEstimateStatus>("idle");
  const gasRequestRef = React.useRef(0);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  // 마지막으로 추정한 "풀:금액" 키. 포트폴리오 2초 폴링으로 positions 배열이
  // 갈리면서 selected 객체 참조가 매번 바뀌어도, 같은 입력이면 재추정하지 않는다.
  const lastEstimateKeyRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  // Withdraw 버튼이 풀 단위로 열리므로 대상은 initialPoolAddress로 고정된다.
  const selected = React.useMemo(() => {
    const key = initialPoolAddress?.toLowerCase();
    return (
      positions.find((p) => p.poolAddress.toLowerCase() === key) ??
      positions[0] ??
      null
    );
  }, [positions, initialPoolAddress]);

  const depositedRaw = React.useMemo(() => {
    try {
      return BigInt(selected?.depositedRaw ?? "0");
    } catch {
      return 0n;
    }
  }, [selected?.depositedRaw]);

  const decimals = selected?.decimals ?? 18;

  React.useEffect(() => {
    if (!open || !selected) return;
    trackFunnel?.("position_selected");
  }, [open, selected, trackFunnel]);

  const resetGas = React.useCallback(() => {
    setGasEstimate(null);
    setGasStatus(estimateWithdraw ? "idle" : "unavailable");
    gasRequestRef.current += 1;
    lastEstimateKeyRef.current = null;
    // estimateWithdraw 콜백 정체성 변화로 상태를 리셋하지 않도록 참조만 쓴다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 다이얼로그가 새로 열릴 때 가스 상태 초기화.
  React.useEffect(() => {
    if (!open) return;
    resetGas();
  }, [open, resetGas]);

  // 출금 금액은 항상 예치 전량 — 선택이 잡히면 디바운스 후 가스 추정.
  // 같은 풀·금액 조합은 한 번만 추정한다(포트폴리오 폴링이 selected 참조만
  // 바꿔도 재추정하지 않도록).
  React.useEffect(() => {
    if (!open || status !== "confirm") {
      clearTimeout(debounceRef.current);
      return;
    }

    if (!estimateWithdraw || !selected) {
      clearTimeout(debounceRef.current);
      setGasStatus("unavailable");
      return;
    }
    if (depositedRaw <= 0n) {
      clearTimeout(debounceRef.current);
      gasRequestRef.current += 1;
      lastEstimateKeyRef.current = null;
      setGasEstimate(null);
      setGasStatus("idle");
      return;
    }

    const estimateKey = `${selected.poolAddress.toLowerCase()}:${depositedRaw.toString()}`;
    if (lastEstimateKeyRef.current === estimateKey) return;
    lastEstimateKeyRef.current = estimateKey;

    clearTimeout(debounceRef.current);
    const requestId = gasRequestRef.current + 1;
    gasRequestRef.current = requestId;
    setGasStatus("loading");

    debounceRef.current = setTimeout(() => {
      estimateWithdraw(selected, depositedRaw)
        .then((estimate) => {
          if (gasRequestRef.current !== requestId) return;
          setGasEstimate(estimate ?? null);
          setGasStatus(estimate ? "ready" : "unavailable");
        })
        .catch(() => {
          if (gasRequestRef.current !== requestId) return;
          setGasEstimate(null);
          setGasStatus("error");
        });
    }, GAS_ESTIMATE_DEBOUNCE_MS);
  }, [open, status, depositedRaw, estimateWithdraw, selected]);

  if (!open || !selected) return null;

  const isSubmitting = status === "submitting";
  const priorityFee = formatGweiFromWei(gasEstimate?.maxPriorityFeePerGas);
  const maxFee = formatGweiFromWei(
    gasEstimate?.maxFeePerGas ?? gasEstimate?.gasPrice,
  );
  const gasLimit = formatGasLimit(gasEstimate?.gasLimit);
  const depositedDisplay = formatPortfolioTokenAmount(
    selected.depositedRaw,
    decimals,
  );

  return (
    <div
      className="wp-claim-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSubmitting) {
          onClose();
        }
      }}
    >
      <section
        className="wp-claim-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wp-withdraw-title"
        aria-label="Withdraw"
        data-status={status}
        data-vaul-no-drag
      >
        <div className="wp-claim-grabber" aria-hidden>
          <span />
        </div>

        <div className="wp-claim-content wp-withdraw-content">
          {status === "success" && txHash ? (
            <>
              <span
                className="wp-claim-icon wp-claim-icon--success"
                aria-hidden
              >
                <SuccessCheckIcon />
              </span>

              <div className="wp-claim-copy">
                <h3 id="wp-withdraw-title" className="wp-claim-title">
                  Withdraw Submitted!
                </h3>
                <p className="wp-claim-desc">
                  Your withdrawal request has been submitted. Balances will
                  update shortly after the transaction settles.
                </p>
              </div>

              <div className="wp-claim-list" aria-label="Withdraw transaction">
                <div className="wp-claim-list-item">
                  <span>Tx Hash</span>
                  <button
                    type="button"
                    className="wp-claim-hash"
                    onClick={onCopyHash}
                    aria-label="Copy transaction hash"
                    data-track="withdraw_hash_copy"
                  >
                    <strong>{shortenTransactionHash(txHash)}</strong>
                    {copied ? <CheckIcon /> : <ClipboardIcon />}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {status === "error" && (
                <div className="wp-claim-heading">
                  <h3 id="wp-withdraw-title" className="wp-claim-title">
                    Withdraw Failed
                  </h3>
                  <p className="wp-claim-error-message">
                    {error || "The withdraw request could not be completed."}
                  </p>
                </div>
              )}

              <div
                className="wp-claim-reward-card"
                data-fit={rewardCardFitLevel(
                  selected.tokenSymbol,
                  depositedDisplay,
                )}
                aria-label="Deposited balance"
              >
                <span className="wp-claim-reward-token" aria-hidden>
                  <TokenBadge position={selected} size={28} />
                </span>
                <strong className="wp-claim-reward-symbol">
                  {selected.tokenSymbol}
                </strong>
                <strong className="wp-claim-reward-amount">
                  {depositedDisplay}
                </strong>
              </div>

              <div
                className="wp-claim-account-card"
                aria-label="Withdraw gas fee"
              >
                <div className="wp-claim-account-row">
                  <div className="wp-claim-account">
                    <span className="wp-claim-account-avatar" aria-hidden />
                    <strong>{selected.tokenSymbol} Pool</strong>
                  </div>
                  <span>{formatWalletAddress(walletAddress)}</span>
                </div>
                <span className="wp-claim-divider" aria-hidden />
                <div className="wp-claim-detail-list">
                  <div className="wp-claim-detail-row">
                    <span>Est. Tx Fee</span>
                    <strong>
                      {formatFeeValue(gasEstimate, gasStatus, nativeSymbol)}
                    </strong>
                  </div>
                  <div className="wp-claim-detail-row wp-claim-detail-row--sub">
                    <span>Priority Fee Per Gas</span>
                    <strong>
                      {formatGasMetric(priorityFee, "Gwei", gasStatus)}
                    </strong>
                  </div>
                  <div className="wp-claim-detail-row wp-claim-detail-row--sub">
                    <span>Max Fee Per Gas</span>
                    <strong>{formatGasMetric(maxFee, "Gwei", gasStatus)}</strong>
                  </div>
                  <div className="wp-claim-detail-row wp-claim-detail-row--sub">
                    <span>Gas Limit</span>
                    <strong>{formatGasMetric(gasLimit, "", gasStatus)}</strong>
                  </div>
                </div>
              </div>
            </>
          )}

          <div
            className="wp-claim-actions"
            data-count={status === "confirm" || status === "error" ? 2 : 1}
          >
            {(status === "confirm" || status === "error") && (
              <button
                type="button"
                className="wp-claim-secondary"
                data-track="withdraw_cancel"
                onClick={onClose}
              >
                {status === "error" ? "Close" : "Cancel"}
              </button>
            )}
            <button
              type="button"
              className="wp-claim-primary"
              data-track="withdraw_confirm"
              onClick={() => {
                if (status === "success") {
                  onClose();
                  return;
                }
                if (selected && depositedRaw > 0n) {
                  onConfirm(selected, depositedRaw, gasEstimate);
                }
              }}
              disabled={
                isSubmitting ||
                ((status === "confirm" || status === "error") &&
                  depositedRaw <= 0n)
              }
            >
              {status === "success" && "Done"}
              {status === "submitting" && (
                <span
                  className="wp-claim-button-spinner"
                  aria-label="Waiting"
                />
              )}
              {status === "error" && "Try Again"}
              {status === "confirm" && "Withdraw Now"}
            </button>
          </div>
        </div>

        <div className="wp-claim-home-indicator" aria-hidden>
          <span />
        </div>
      </section>
    </div>
  );
}
